import React, { useState } from 'react';
import { useApp, api, MEDIA } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BookOpen, Layers, FileText, Activity, Film, Image as ImageIcon, ClipboardCheck,
  ClipboardList, FileEdit, Home, Award, HelpCircle, Heart, MessageCircle, Gamepad2,
  Sparkles, Loader2, X, Download, Video
} from 'lucide-react';

// Catalog of generators with field schemas
const VOICES = [
  { v: 'nova', label: 'Nova' },
  { v: 'alloy', label: 'Alloy' },
  { v: 'shimmer', label: 'Shimmer' },
  { v: 'echo', label: 'Echo' },
  { v: 'fable', label: 'Fable' },
  { v: 'onyx', label: 'Onyx' },
];

// Per-field option-source mapping: which dropdown source to use for a given field name
const FIELD_OPTIONS = {
  disability_type: 'disability_types',
  disability_level: 'disability_levels',
  age_group: 'age_groups',
  sport_activity: 'sport_activities',
  skill_focus: 'skill_focus',
  skill: 'skill_focus',
  performance_level: 'performance_levels',
  focus: 'skill_focus',
};

const GENERATORS = [
  { key: 'story', endpoint: '/ai/story', icon: BookOpen, color: 'bg-purple-100 text-purple-700',
    fields: [
      { name: 'topic', type: 'text', required: true, placeholder: 'مثال: مغامرة الكوتش حمدان في درس التوازن' },
      { name: 'age_group', type: 'select', required: true },
      { name: 'disability_type', type: 'select' },
      { name: 'scenes', type: 'number', default: 4, min: 2, max: 6 },
      { name: 'voice', type: 'voice' },
      { name: 'with_images', type: 'bool', default: true },
      { name: 'with_audio', type: 'bool', default: true },
    ] },
  { key: 'flashcards', endpoint: '/ai/flashcards', icon: Layers, color: 'bg-amber-100 text-amber-700',
    fields: [
      { name: 'topic', type: 'text', required: true, placeholder: 'مثال: مكونات اللياقة البدنية' },
      { name: 'count', type: 'number', default: 6, min: 3, max: 12 },
      { name: 'with_images', type: 'bool', default: true },
      { name: 'with_audio', type: 'bool', default: false },
    ] },
  { key: 'lesson', endpoint: '/ai/lesson-plan', icon: FileText, color: 'bg-emerald-100 text-emerald-700',
    fields: [
      { name: 'disability_type', type: 'select', required: true },
      { name: 'age_group', type: 'select', required: true },
      { name: 'sport_activity', type: 'select', required: true },
      { name: 'performance_level', type: 'select', default: 'متوسط' },
      { name: 'extra_context', type: 'longtext' },
    ] },
  { key: 'activity', endpoint: '/ai/activity', icon: Activity, color: 'bg-sky-100 text-sky-700',
    fields: [
      { name: 'disability_type', type: 'select', required: true },
      { name: 'age_group', type: 'select', required: true },
      { name: 'skill_focus', type: 'select', required: true },
    ] },
  { key: 'video', endpoint: '/ai/video-script', icon: Film, color: 'bg-rose-100 text-rose-700',
    fields: [
      { name: 'topic', type: 'text', required: true, placeholder: 'مثال: كيف نسخّن العضلات قبل الرياضة' },
      { name: 'age_group', type: 'select', default: 'ابتدائية دنيا (7–9)' },
      { name: 'frames', type: 'number', default: 6, min: 4, max: 8 },
      { name: 'voice', type: 'voice' },
      { name: 'with_audio', type: 'bool', default: true },
    ] },
  { key: 'visual', endpoint: '/ai/visual-demo', icon: ImageIcon, color: 'bg-fuchsia-100 text-fuchsia-700',
    fields: [
      { name: 'prompt', type: 'text', required: true, placeholder: 'مثال: طفل يتدرب على التوازن على قدم واحدة' },
      { name: 'count', type: 'number', default: 3, min: 1, max: 4 },
    ] },
  { key: 'assessment', endpoint: '/ai/assessment', icon: ClipboardCheck, color: 'bg-teal-100 text-teal-700',
    fields: [
      { name: 'skill', type: 'select', required: true },
      { name: 'age_group', type: 'select', required: true },
    ] },
  { key: 'iep', endpoint: '/ai/iep', icon: ClipboardList, color: 'bg-indigo-100 text-indigo-700',
    fields: [
      { name: 'student_name', type: 'text', required: true },
      { name: 'disability_type', type: 'select', required: true },
      { name: 'disability_level', type: 'select', required: true, default: 'إعاقة متوسطة' },
      { name: 'age_group', type: 'select', required: true },
      { name: 'strengths', type: 'longtext' },
      { name: 'needs', type: 'longtext' },
    ] },
  { key: 'worksheet', endpoint: '/ai/worksheet', icon: FileEdit, color: 'bg-orange-100 text-orange-700',
    fields: [
      { name: 'topic', type: 'text', required: true, placeholder: 'مثال: قواعد السلامة في صالة الرياضة' },
      { name: 'age_group', type: 'select', required: true },
    ] },
  { key: 'family', endpoint: '/ai/family-activities', icon: Home, color: 'bg-pink-100 text-pink-700',
    fields: [
      { name: 'child_age', type: 'text', required: true, default: '7' },
      { name: 'disability_type', type: 'select', required: true },
      { name: 'focus', type: 'select' },
    ] },
  { key: 'certificate', endpoint: '/ai/certificate', icon: Award, color: 'bg-yellow-100 text-yellow-700',
    fields: [
      { name: 'recipient_name', type: 'text', required: true },
      { name: 'achievement', type: 'text', required: true, default: 'إتقان مهارة التوازن' },
      { name: 'date', type: 'text' },
    ] },
  { key: 'quiz', endpoint: '/ai/quiz', icon: HelpCircle, color: 'bg-violet-100 text-violet-700',
    fields: [
      { name: 'topic', type: 'text', required: true, placeholder: 'مثال: التغذية الرياضية والترطيب' },
      { name: 'age_group', type: 'select', required: true },
      { name: 'questions', type: 'number', default: 5, min: 3, max: 10 },
    ] },
  { key: 'adaptive', endpoint: '/ai/adaptive-activities', icon: Heart, color: 'bg-red-100 text-red-700',
    fields: [
      { name: 'skill', type: 'select', required: true },
      { name: 'age_group', type: 'select', required: true },
    ] },
  { key: 'coach', endpoint: '/ai/coach', icon: MessageCircle, color: 'bg-cyan-100 text-cyan-700',
    fields: [
      { name: 'question', type: 'longtext', required: true, placeholder: 'مثال: كيف أحفّز طالب التوحّد على المشاركة في الإحماء؟' },
      { name: 'voice', type: 'voice' },
      { name: 'with_audio', type: 'bool', default: true },
    ] },
  { key: 'game', endpoint: '/ai/game', icon: Gamepad2, color: 'bg-lime-100 text-lime-700',
    fields: [
      { name: 'topic', type: 'text', required: true, placeholder: 'مثال: لعبة التوازن مع الأكياس' },
      { name: 'age_group', type: 'select', required: true },
    ] },
  { key: 'video_sora', endpoint: '/ai/video', icon: Video, color: 'bg-rose-100 text-rose-700', longRun: true,
    fields: [
      { name: 'prompt', type: 'longtext', required: true, placeholder: 'مثال: طالب يمارس تمرين التوازن على قدم واحدة في صالة رياضية مضيئة، لقطة جانبية واضحة، ٤ ثوانٍ' },
      { name: 'provider', type: 'select-static', default: 'fal-ltx', options: [
          { v: 'fal-ltx', label: '⚡ LTX (سريع + اقتصادي)' },
          { v: 'fal-kling', label: '🎬 Kling 1.6 (جودة أعلى)' },
          { v: 'sora2', label: '🌟 Sora 2 (أعلى جودة — أغلى)' },
      ]},
      { name: 'duration', type: 'select-static', default: 5, options: [
          { v: 4, label: '4s' }, { v: 5, label: '5s' }, { v: 8, label: '8s' }, { v: 10, label: '10s' }, { v: 12, label: '12s' },
      ]},
      { name: 'aspect_ratio', type: 'select-static', default: '16:9', options: [
          { v: '16:9', label: 'أفقي 16:9' }, { v: '9:16', label: 'عمودي 9:16' }, { v: '1:1', label: 'مربّع 1:1' },
      ]},
      { name: 'size', type: 'select-static', default: '1280x720', options: [
          { v: '1280x720', label: 'HD أفقي (Sora)' }, { v: '1024x1792', label: 'عمودي موبايل (Sora)' },
          { v: '1792x1024', label: 'واسع (Sora)' }, { v: '1024x1024', label: 'مربّع (Sora)' },
      ]},
      { name: 'model', type: 'select-static', default: 'sora-2', options: [
          { v: 'sora-2', label: 'Sora 2 (سريع)' }, { v: 'sora-2-pro', label: 'Sora 2 Pro (جودة أعلى)' },
      ]},
    ] },
];

export default function AIStudioPage() {
  const { t, lang } = useApp();
  const [active, setActive] = useState(null);
  const [result, setResult] = useState(null);
  const [options, setOptions] = useState(null);

  React.useEffect(() => {
    api.get('/ai/domain/options').then(r => setOptions(r.data)).catch(() => {});
  }, []);

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="ai-studio-page">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
          <Sparkles className="w-3 h-3" /> AI · {lang === 'ar' ? 'التربية البدنية واللياقة' : 'PE & Fitness'}
        </div>
        <h1 className="text-3xl sm:text-4xl font-black mb-2">{t('ai_studio')}</h1>
        <p className="text-muted-foreground">{lang === 'ar' ? 'مولّدات ذكاء اصطناعي متخصصة في التربية البدنية المعدّلة، الصحة، واللياقة لأصحاب الهمم بكل فئاتهم.' : 'AI generators specialised in adapted PE, health, and fitness for People of Determination — all disability categories.'}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {GENERATORS.map((g) => {
          const Icon = g.icon;
          return (
            <button
              key={g.key}
              onClick={() => setActive(g)}
              data-testid={`gen-card-${g.key}`}
              className="text-start card-elevate bg-white border border-border rounded-xl p-5 hover:border-primary transition-colors"
            >
              <div className={`w-12 h-12 rounded-lg ${g.color} grid place-items-center mb-3`}>
                <Icon className="w-6 h-6" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-base mb-1">{t('gen_' + g.key)}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2">{g.endpoint}</p>
            </button>
          );
        })}
      </div>

      <GeneratorDialog generator={active} options={options} onClose={() => setActive(null)} onResult={(r) => { setActive(null); setResult(r); }} />
      <ResultDialog result={result} onClose={() => setResult(null)} />
    </main>
  );
}

function GeneratorDialog({ generator, options, onClose, onResult }) {
  const { t, lang } = useApp();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (generator) {
      const init = { language: lang };
      generator.fields.forEach(f => {
        if (f.default !== undefined) init[f.name] = f.default;
        else if (f.type === 'bool') init[f.name] = false;
        else if (f.type === 'voice') init[f.name] = 'nova';
        else init[f.name] = '';
      });
      setForm(init);
    }
  }, [generator, lang]);

  if (!generator) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(generator.endpoint, form);
      onResult({ ...data, _kind: generator.key });
      toast.success(t('saved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const getOptionsFor = (fieldName) => {
    const key = FIELD_OPTIONS[fieldName];
    if (!key || !options) return [];
    return options[key]?.[lang] || options[key]?.ar || [];
  };

  return (
    <Dialog open={!!generator} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="generator-dialog">
        <DialogHeader>
          <DialogTitle>{t('gen_' + generator.key)}</DialogTitle>
          <DialogDescription className="sr-only">Generator form</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Curriculum goal picker (auto-fill from goal) */}
          <GoalPicker value={form.goal_id} onPick={(g) => {
            if (!g) { setForm({ ...form, goal_id: '' }); return; }
            const patch = { ...form, goal_id: g.id };
            if ('disability_type' in form && !form.disability_type) patch.disability_type = g.disability_type_ar;
            if ('age_group' in form && !form.age_group) patch.age_group = g.age_group_ar;
            if ('sport_activity' in form && !form.sport_activity) patch.sport_activity = g.sport_activity_ar;
            if ('skill_focus' in form && !form.skill_focus) patch.skill_focus = g.sub_goal_ar || g.sport_activity_ar;
            if ('skill' in form && !form.skill) patch.skill = g.sub_goal_ar || g.main_goal_ar;
            if ('topic' in form && !form.topic) patch.topic = g.sub_goal_ar || g.main_goal_ar;
            if ('focus' in form && !form.focus) patch.focus = g.sub_goal_ar;
            if ('performance_level' in form && !form.performance_level) patch.performance_level = g.performance_level_ar;
            if ('disability_level' in form && !form.disability_level) patch.disability_level = g.disability_level_ar;
            setForm(patch);
          }} />

          {generator.fields.map(f => (
            <div key={f.name}>
              <Label className="text-xs capitalize">{labelFor(f.name, lang)}{f.required ? ' *' : ''}</Label>
              {f.type === 'longtext' ? (
                <Textarea value={form[f.name] || ''} onChange={(e) => set(f.name, e.target.value)} rows={3} placeholder={f.placeholder} data-testid={`gen-field-${f.name}`} />
              ) : f.type === 'number' ? (
                <Input type="number" min={f.min} max={f.max} value={form[f.name] ?? ''} onChange={(e) => set(f.name, Number(e.target.value))} data-testid={`gen-field-${f.name}`} />
              ) : f.type === 'bool' ? (
                <div className="flex items-center gap-2 pt-1"><Switch checked={!!form[f.name]} onCheckedChange={(v) => set(f.name, v)} data-testid={`gen-field-${f.name}`} /><span className="text-sm text-muted-foreground">{form[f.name] ? 'ON' : 'OFF'}</span></div>
              ) : f.type === 'voice' ? (
                <Select value={form[f.name] || 'nova'} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`gen-field-${f.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{VOICES.map(v => <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === 'select' ? (
                <Select value={form[f.name] || ''} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`gen-field-${f.name}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{getOptionsFor(f.name).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === 'select-static' ? (
                <Select value={String(form[f.name] ?? f.default ?? '')} onValueChange={(v) => set(f.name, isNaN(Number(v)) ? v : Number(v))}>
                  <SelectTrigger data-testid={`gen-field-${f.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{(f.options || []).map(o => <SelectItem key={String(o.v)} value={String(o.v)}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={form[f.name] || ''} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} data-testid={`gen-field-${f.name}`} />
              )}
            </div>
          ))}
          <Button onClick={submit} disabled={busy} className="w-full bg-primary h-11" data-testid="generator-submit">
            {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" /> {t('generating')}</> : <><Sparkles className="w-4 h-4 me-2" /> {t('generate')}</>}
          </Button>
          {busy && <p className="text-xs text-muted-foreground text-center">
            {generator?.longRun
              ? (lang === 'ar' ? 'توليد الفيديو بالذكاء الاصطناعي قد يستغرق 2–10 دقائق حسب الجودة والمدة، الرجاء عدم إغلاق النافذة.' : 'AI video generation can take 2–10 minutes depending on quality and duration. Please do not close the window.')
              : (lang === 'ar' ? 'قد يستغرق توليد الصور والصوت دقيقة أو أكثر...' : 'Image + audio generation may take a minute or more...')}
          </p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FIELD_LABELS_AR = {
  topic: 'الموضوع', age_group: 'الفئة العمرية', disability_type: 'نوع الإعاقة', disability_level: 'مستوى الإعاقة',
  sport_activity: 'الرياضة/النشاط', skill: 'المهارة', skill_focus: 'مهارة التركيز', performance_level: 'مستوى الأداء',
  scenes: 'عدد المشاهد', frames: 'عدد اللقطات', count: 'العدد', questions: 'عدد الأسئلة',
  voice: 'الصوت', with_images: 'مع صور', with_audio: 'مع صوت',
  student_name: 'اسم الطالب', recipient_name: 'اسم المستلم', achievement: 'الإنجاز', date: 'التاريخ',
  strengths: 'نقاط القوة', needs: 'الاحتياجات', extra_context: 'سياق إضافي', focus: 'التركيز',
  child_age: 'عمر الطفل', question: 'السؤال', prompt: 'الوصف',
  duration: 'المدة', size: 'المقاس', model: 'النموذج',
  provider: 'مزوّد التوليد', aspect_ratio: 'نسبة العرض',
};
const FIELD_LABELS_EN = {
  topic: 'Topic', age_group: 'Age group', disability_type: 'Disability type', disability_level: 'Severity',
  sport_activity: 'Sport / Activity', skill: 'Skill', skill_focus: 'Skill focus', performance_level: 'Performance level',
  scenes: 'Scenes', frames: 'Frames', count: 'Count', questions: 'Questions',
  voice: 'Voice', with_images: 'With images', with_audio: 'With audio',
  student_name: 'Student name', recipient_name: 'Recipient name', achievement: 'Achievement', date: 'Date',
  strengths: 'Strengths', needs: 'Needs', extra_context: 'Extra context', focus: 'Focus',
  child_age: 'Child age', question: 'Question', prompt: 'Prompt',
  duration: 'Duration', size: 'Aspect / size', model: 'Model',
  provider: 'Provider', aspect_ratio: 'Aspect ratio',
};
function labelFor(name, lang) {
  const dict = lang === 'ar' ? FIELD_LABELS_AR : FIELD_LABELS_EN;
  return dict[name] || name.replace(/_/g, ' ');
}

function GoalPicker({ value, onPick }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(null);

  // load picked goal preview on mount if a value exists
  React.useEffect(() => {
    if (value && (!picked || picked.id !== value)) {
      api.get(`/curriculum/goals/${value}`).then(r => setPicked(r.data)).catch(() => {});
    }
    if (!value && picked) setPicked(null);
  }, [value]);

  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get('/curriculum/goals', { params: { q: q || undefined, limit: 12 } }).then(r => setItems(r.data.items)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div className="bg-secondary/40 rounded-lg p-3 border border-border" data-testid="goal-picker">
      <div className="flex items-start gap-2 mb-2">
        <span className="text-base">🎯</span>
        <div className="flex-1">
          <div className="text-xs font-bold mb-0.5">{lang === 'ar' ? 'ربط بهدف من المنهج (اختياري)' : 'Link to curriculum goal (optional)'}</div>
          <div className="text-[10px] text-muted-foreground">{lang === 'ar' ? 'يملأ الحقول تلقائياً ويحفظ النتيجة مرتبطة بالهدف' : 'Auto-fills fields and links the output to the goal'}</div>
        </div>
      </div>

      {picked ? (
        <div className="flex items-center gap-2 bg-white rounded p-2 border border-primary/30" data-testid="picked-goal">
          <Badge variant="secondary" className="font-mono text-[10px] shrink-0">{picked.goal_code}</Badge>
          <span className="text-xs font-medium line-clamp-1 flex-1">{lang === 'ar' ? picked.main_goal_ar : (picked.main_goal_en || picked.main_goal_ar)}</span>
          <button onClick={() => { setPicked(null); onPick(null); }} aria-label="clear" data-testid="clear-goal" className="text-muted-foreground hover:text-destructive p-1"><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" className="w-full h-8 text-xs" onClick={() => setOpen(true)} data-testid="open-goal-picker">
          🔍 {lang === 'ar' ? 'اختر هدفاً من المنهج' : 'Pick a curriculum goal'}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="goal-picker-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'ar' ? 'اختر هدفاً' : 'Pick a goal'}</DialogTitle>
            <DialogDescription className="sr-only">Goal selection</DialogDescription>
          </DialogHeader>
          <Input placeholder={lang === 'ar' ? 'ابحث في الأهداف...' : 'Search goals...'} value={q} onChange={(e) => setQ(e.target.value)} data-testid="goal-picker-search" />
          <div className="flex-1 overflow-y-auto space-y-2 mt-2">
            {items.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-8">{lang === 'ar' ? 'لا نتائج' : 'No results'}</p>
            ) : items.map(g => (
              <button
                key={g.id}
                onClick={() => { setPicked(g); onPick(g); setOpen(false); }}
                data-testid={`pick-${g.goal_code}`}
                className="w-full text-start border border-border rounded-lg p-3 hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="font-mono text-[10px]">{g.goal_code}</Badge>
                  <Badge className="bg-primary/10 text-primary text-[10px]" variant="outline">{g.performance_level_ar}</Badge>
                </div>
                <div className="font-semibold text-sm line-clamp-2 mb-1">{lang === 'ar' ? g.main_goal_ar : (g.main_goal_en || g.main_goal_ar)}</div>
                <div className="text-[10px] text-muted-foreground">{g.disability_type_ar} · {g.age_group_ar}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultDialog({ result, onClose }) {
  const { t, lang } = useApp();
  if (!result) return null;
  const copy = async () => {
    const { artifactToText } = await import('@/lib/artifact-export');
    try { await navigator.clipboard.writeText(artifactToText(result, lang)); toast.success(lang === 'ar' ? 'تم النسخ' : 'Copied'); } catch { toast.error('error'); }
  };
  const exportPdf = async () => {
    const { exportPackPdf } = await import('@/lib/artifact-export');
    exportPackPdf({ artifacts: { [result._kind || result.kind]: result }, lang });
  };
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="result-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <DialogTitle className="flex items-center gap-2">
              <span>{t('gen_' + result._kind)}</span>
              <Badge variant="secondary">{result._kind}</Badge>
            </DialogTitle>
            <div className="flex gap-2 me-6">
              <Button variant="outline" size="sm" onClick={copy} data-testid="result-copy">📋 {lang === 'ar' ? 'نسخ' : 'Copy'}</Button>
              <Button variant="outline" size="sm" onClick={exportPdf} data-testid="result-pdf">📄 PDF</Button>
            </div>
          </div>
          <DialogDescription className="sr-only">Generated artifact</DialogDescription>
        </DialogHeader>
        <ArtifactView a={result} />
      </DialogContent>
    </Dialog>
  );
}

export function ArtifactView({ a }) {
  const { t, lang } = useApp();
  const kind = a.kind || a._kind;

  if (kind === 'video' && a.video_url) {
    return (
      <div className="space-y-3" data-testid="video-artifact">
        <div className="rounded-xl overflow-hidden border-2 border-border bg-black">
          <video src={MEDIA(a.video_url)} controls autoPlay loop className="w-full max-h-[70vh]" data-testid="ai-video-player">
            {lang === 'ar' ? 'المتصفح لا يدعم تشغيل الفيديو.' : 'Your browser does not support video playback.'}
          </video>
        </div>
        <div className="bg-stone-50 rounded-lg p-3 text-sm space-y-1">
          <div className="font-bold">{lang === 'ar' ? 'وصف المطالبة' : 'Prompt'}:</div>
          <div className="text-muted-foreground whitespace-pre-wrap">{a.prompt}</div>
          <div className="text-[11px] text-muted-foreground pt-1">
            {a.model || 'sora-2'} · {a.size || '1280x720'} · {a.duration || 4}s
          </div>
        </div>
        <Button asChild variant="outline" size="sm" data-testid="download-video-btn">
          <a href={MEDIA(a.video_url)} download={a.file_name || 'video.mp4'}>
            <Download className="w-4 h-4 me-2" />{lang === 'ar' ? 'تحميل الفيديو' : 'Download video'}
          </a>
        </Button>
      </div>
    );
  }

  if (kind === 'story' || kind === 'video_script' || kind === 'video') {
    const arr = a.scenes || a.frames || [];
    return (
      <div className="space-y-4">
        {a.title && <h3 className="text-xl font-black">{a.title}</h3>}
        {a.moral && <p className="italic text-muted-foreground">{a.moral}</p>}
        <div className="grid sm:grid-cols-2 gap-4">
          {arr.map((s, i) => (
            <div key={i} className="border border-border rounded-lg overflow-hidden" data-testid={`scene-${i}`}>
              {s.image_url && <img src={MEDIA(s.image_url)} alt={`scene ${i+1}`} className="w-full aspect-video object-cover" loading="lazy" />}
              <div className="p-3">
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{s.text || s.narration}</div>
                {s.audio_url && <audio controls className="mt-2 w-full" src={MEDIA(s.audio_url)} data-testid={`audio-${i}`} />}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'flashcards') {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(a.cards || []).map((c, i) => (
          <div key={i} className="border border-border rounded-lg overflow-hidden" data-testid={`card-${i}`}>
            {c.image_url && <img src={MEDIA(c.image_url)} alt={c.front} className="w-full aspect-square object-cover" loading="lazy" />}
            <div className="p-3">
              <div className="font-bold mb-1">{c.front}</div>
              <div className="text-sm text-muted-foreground">{c.back}</div>
              {c.audio_url && <audio controls className="mt-2 w-full" src={MEDIA(c.audio_url)} />}
            </div>
          </div>
        ))}
        {(a.cards || []).length > 0 && (
          <div className="col-span-full flex justify-end">
            <Button size="sm" variant="outline" onClick={() => printFlashcards(a, lang)} data-testid="flashcards-print"><Download className="w-4 h-4 me-1" />PDF</Button>
          </div>
        )}
      </div>
    );
  }

  if (kind === 'lesson_plan' || kind === 'activity' || kind === 'iep') {
    return <pre className="whitespace-pre-wrap text-sm leading-relaxed bg-secondary/40 p-4 rounded-lg" data-testid="text-output">{a.content}</pre>;
  }

  if (kind === 'visual_demo') {
    return (
      <div className="grid sm:grid-cols-2 gap-3">
        {(a.images || []).map((u, i) => (
          <img key={i} src={MEDIA(u)} alt={`visual ${i+1}`} className="w-full rounded-lg border border-border" loading="lazy" data-testid={`visual-${i}`} />
        ))}
      </div>
    );
  }

  if (kind === 'assessment') {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-bold">{a.name}</h3>
        <table className="w-full text-sm border-collapse">
          <thead><tr className="bg-primary text-primary-foreground">
            <th className="border border-border p-2 text-start">{lang === 'ar' ? 'المعيار' : 'Criterion'}</th>
            {(a.criteria?.[0]?.levels || []).map((l, i) => <th key={i} className="border border-border p-2">{l.label}</th>)}
          </tr></thead>
          <tbody>
            {(a.criteria || []).map((c, i) => (
              <tr key={i}>
                <td className="border border-border p-2 font-semibold bg-secondary/40">{c.name}{c.weight && c.weight !== 1 ? ` (×${c.weight})` : ''}</td>
                {(c.levels || []).map((l, li) => <td key={li} className="border border-border p-2 align-top text-xs">{l.descriptor}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (kind === 'worksheet') {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-bold">{a.title}</h3>
        <p className="text-sm text-muted-foreground">{a.instructions}</p>
        <ol className="space-y-2 list-decimal list-inside">
          {(a.tasks || []).map((t, i) => (
            <li key={i} className="border border-border rounded p-2 text-sm" data-testid={`task-${i}`}>
              <Badge variant="secondary" className="me-2 text-[10px]">{t.type}</Badge>
              <span className="font-medium">{t.prompt}</span>
              {t.answer && <div className="text-xs text-muted-foreground mt-1">→ {t.answer}</div>}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (kind === 'family_activities') {
    return (
      <div className="space-y-3">
        {(a.activities || []).map((act, i) => (
          <div key={i} className="border border-border rounded-lg p-3" data-testid={`fam-act-${i}`}>
            <div className="flex items-center justify-between mb-2"><h4 className="font-bold">{act.name}</h4><Badge variant="secondary">{act.time_minutes} min</Badge></div>
            <p className="text-xs text-muted-foreground mb-1">{act.materials}</p>
            <ol className="text-sm list-decimal list-inside space-y-1">{(act.steps || []).map((s, si) => <li key={si}>{s}</li>)}</ol>
            <p className="text-xs italic mt-2 text-primary">{act.benefits}</p>
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'certificate') {
    return <CertificateView a={a} />;
  }

  if (kind === 'quiz') {
    return (
      <div className="space-y-3">
        {(a.questions || []).map((q, i) => (
          <div key={i} className="border border-border rounded-lg p-3" data-testid={`q-${i}`}>
            <div className="font-semibold mb-2">{i+1}. {q.q}</div>
            <ul className="space-y-1">
              {(q.options || []).map((o, oi) => (
                <li key={oi} className={`text-sm px-2 py-1 rounded ${oi === q.correct ? 'bg-primary/10 text-primary font-semibold' : 'bg-secondary/30'}`}>{String.fromCharCode(65 + oi)}. {o}</li>
              ))}
            </ul>
            {q.explanation && <p className="text-xs text-muted-foreground mt-2 italic">{q.explanation}</p>}
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'adaptive_activities') {
    return (
      <div className="space-y-3">
        {(a.variants || []).map((v, i) => (
          <div key={i} className="border border-border rounded-lg p-3" data-testid={`variant-${i}`}>
            <Badge className="bg-primary text-primary-foreground mb-2">{v.category}</Badge>
            <p className="text-sm font-semibold">{v.activity}</p>
            <p className="text-xs text-muted-foreground mt-1"><strong>{lang === 'ar' ? 'التكييفات:' : 'Adaptations:'}</strong> {v.key_adaptations}</p>
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'coach') {
    return (
      <div className="space-y-3">
        <div className="bg-secondary/40 rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">{lang === 'ar' ? 'سؤالك' : 'Your question'}</div>
          <div className="text-sm">{a.question}</div>
        </div>
        <div className="bg-primary/10 rounded-lg p-3 border border-primary/30">
          <div className="text-xs text-primary font-semibold mb-1">{lang === 'ar' ? 'كوتش حمدان' : 'Coach Hamdan'}</div>
          <div className="text-sm whitespace-pre-wrap">{a.reply}</div>
        </div>
        {a.audio_url && <audio controls className="w-full" src={MEDIA(a.audio_url)} data-testid="coach-audio" />}
      </div>
    );
  }

  if (kind === 'game') {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-bold">{a.name}</h3>
        <p className="text-sm text-muted-foreground">{a.goal}</p>
        <div className="text-xs">📦 {a.materials}</div>
        <div>
          <div className="font-semibold text-sm mb-1">{lang === 'ar' ? 'القواعد' : 'Rules'}</div>
          <ul className="text-sm list-disc list-inside">{(a.rules || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
        <div>
          <div className="font-semibold text-sm mb-1">{lang === 'ar' ? 'الجولات' : 'Rounds'}</div>
          {(a.rounds || []).map((r, i) => (
            <div key={i} className="border border-border rounded p-2 mb-1 text-sm" data-testid={`round-${i}`}>
              <strong>{r.name}:</strong> {r.prompt} <em className="text-xs text-muted-foreground">({r.scoring})</em>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return <pre className="text-xs overflow-x-auto bg-secondary/40 p-3 rounded">{JSON.stringify(a, null, 2)}</pre>;
}

function CertificateView({ a }) {
  const { t, lang } = useApp();
  const print = () => {
    const w = window.open('', '_blank'); if (!w) return;
    const isAr = lang === 'ar';
    w.document.write(`<!doctype html><html lang="${lang}" dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>Certificate</title>
      <style>body{margin:0;font-family:'Cairo',sans-serif;background:#FDFBF7}
      .frame{padding:60px;background:url('${MEDIA(a.background_url || '')}') center/cover no-repeat;min-height:90vh;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;color:#14532D}
      h1{font-size:48px;margin:0 0 30px}.name{font-size:64px;font-weight:900;color:#B45309;margin:20px 0}
      .ach{font-size:28px;max-width:700px}.date{margin-top:30px;font-size:18px}
      </style></head><body>
      <div class="frame">
        <h1>${isAr ? 'شهادة تقدير' : 'Certificate of Achievement'}</h1>
        <div>${isAr ? 'تُمنح إلى' : 'Awarded to'}</div>
        <div class="name">${a.recipient_name}</div>
        <div class="ach">${isAr ? 'لتفوقه في' : 'For excellence in'}: <strong>${a.achievement}</strong></div>
        <div class="date">${a.date}</div>
        <div style="margin-top:50px;font-size:14px;opacity:.8">${isAr ? 'منصة همم — التربية الرياضية المعدّلة' : 'Himam Platform — Adapted PE'}</div>
      </div><script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };
  return (
    <div className="space-y-3">
      <div className="relative aspect-[7/5] rounded-lg overflow-hidden border-2 border-primary/30">
        {a.background_url && <img src={MEDIA(a.background_url)} alt="certificate" className="absolute inset-0 w-full h-full object-cover opacity-90" />}
        <div className="relative h-full grid place-items-center text-center p-6 bg-white/60">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary mb-2">{lang === 'ar' ? 'شهادة تقدير' : 'Certificate of Achievement'}</div>
            <div className="text-3xl sm:text-5xl font-black text-zayed-ochre mb-2">{a.recipient_name}</div>
            <div className="text-sm sm:text-base">{lang === 'ar' ? 'لتفوقه في' : 'For excellence in'} <strong>{a.achievement}</strong></div>
            <div className="text-xs text-muted-foreground mt-3">{a.date}</div>
          </div>
        </div>
      </div>
      <Button onClick={print} className="bg-primary" data-testid="cert-print"><Download className="w-4 h-4 me-2" />{t('download')} / Print</Button>
    </div>
  );
}

function printFlashcards(a, lang) {
  const w = window.open('', '_blank'); if (!w) return;
  const isAr = lang === 'ar';
  const cardsHtml = (a.cards || []).map(c => `
    <div class="card">
      ${c.image_url ? `<img src="${MEDIA_URL_ABS(c.image_url)}" />` : ''}
      <div class="front">${c.front || ''}</div>
      <div class="back">${c.back || ''}</div>
    </div>
  `).join('');
  w.document.write(`<!doctype html><html lang="${lang}" dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>Flashcards</title>
    <style>body{font-family:'Cairo',sans-serif;padding:20px;background:#FDFBF7}
    h1{color:#14532D}.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px}
    .card{border:2px solid #14532D;border-radius:12px;padding:12px;background:white;page-break-inside:avoid}
    .card img{width:100%;height:160px;object-fit:cover;border-radius:8px;margin-bottom:8px}
    .front{font-size:20px;font-weight:900;color:#14532D}
    .back{font-size:14px;color:#444;margin-top:6px}
    </style></head><body><h1>${a.topic || (isAr ? 'بطاقات تعليمية' : 'Flashcards')}</h1>
    <div class="grid">${cardsHtml}</div>
    <script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

function MEDIA_URL_ABS(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}
